import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useChartTheme } from '../../../utils/chartTheme';
import { RATING_TONE } from './feedbackBands';

// The key rows under the bars are the ratings ring's own (.bd-key).
import './band-donut.css';
import './feedback-list.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

/**
 * Good reviews against bad, as two bars side by side.
 *
 * `groups` is the summary's `reviewGroups` -- [{ key, label, value, share }]
 * for good (4-5) and bad (1-3) -- counted by the API over every review that
 * matches the page's filters, not the rows this page has loaded. This only
 * draws them. The bars keep the rating tones (feedbackBands RATING_TONE):
 * the colour is the verdict, so it never follows the palette.
 */
const ReviewCompareChart = ({ groups = [] }) => {
  const theme = useChartTheme();
  const rows = useMemo(
    () =>
      ['good', 'bad']
        .map((key) => (Array.isArray(groups) ? groups : []).find((g) => g.key === key))
        .filter(Boolean),
    [groups],
  );
  const total = rows.reduce((n, g) => n + (Number(g.value) || 0), 0);

  const data = useMemo(
    () => ({
      labels: rows.map((g) => g.label),
      datasets: [
        {
          data: rows.map((g) => Number(g.value) || 0),
          backgroundColor: rows.map((g) => (g.key === 'good' ? RATING_TONE.happy : RATING_TONE.unhappy)),
          hoverBackgroundColor: rows.map((g) => (g.key === 'good' ? RATING_TONE.happy : RATING_TONE.unhappy)),
          // Rounded at the value, square at the baseline it grows from.
          borderRadius: 6,
          borderSkipped: 'start',
          maxBarThickness: 56,
        },
      ],
    }),
    [rows],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 260 },
      plugins: {
        // One series, named on the axis: a legend would only repeat it.
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => {
              const g = rows[ctx.dataIndex] || {};
              return ` ${g.value} of ${total} feedbacks (${g.share ?? 0}%)`;
            },
          },
        },
      },
      scales: {
        x: theme.categoryAxis(),
        y: theme.valueAxis({ beginAtZero: true, ticks: { precision: 0 } }),
      },
    }),
    [rows, total, theme],
  );

  // An empty pair of bars reads as a chart that failed, not as "nothing yet".
  if (!total) return <p className="bd-empty">No feedbacks to chart yet.</p>;

  return (
    <div className="rvc">
      <div className="rvc-plot">
        <Bar data={data} options={options} />
      </div>
      {/* The same figures in text, readable without hovering. */}
      <ul className="bd-key">
        {rows.map((g) => (
          <li className="bd-key-row" key={g.key}>
            <span
              className="bd-key-dot"
              style={{ background: g.key === 'good' ? RATING_TONE.happy : RATING_TONE.unhappy }}
            />
            <span className="bd-key-label">{g.label}</span>
            <b className="bd-key-n">
              {g.value} <small>({g.share ?? 0}%)</small>
            </b>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ReviewCompareChart;
